import type { ChannelClient } from '@hibro/platform-core';
import type {
  IModuleService,
  IOverlayService,
  OverlayApi,
  OverlayOpen,
  OverlayRequest,
  OverlayResolved,
  OverlayResult,
  ResolvedView,
} from '@hibro/shared';
import { OVERLAY_OPEN_EVENT, OVERLAY_RESOLVED_EVENT } from '@hibro/shared';
import {
  AlertDialog,
  ConfirmDialog,
  Modal,
  PromptDialog,
  SelectDialog,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { usePrepareNativeContentsSuppression } from '../native-contents';
import { type UiRegistry } from '../runtime/ui-registry';
import { OverlayContext } from './overlay-context';
import { shouldPresentHostWindow } from './overlay-window-focus';
import { SurfaceView } from './surface-host';
/**
 * One live overlay on the stack (架构方案 §5.8 OverlaySurface). A view-initiated
 * open carries `localResolve` (resolves the caller's in-process Promise); a
 * host-initiated open carries `hostId` (answered over the RPC channel so the
 * ext-host waiter resolves). Both are settled through {@link resolveInstance}.
 */
interface OverlayInstance {
  key: string;
  request: OverlayRequest;
  hostId?: string;
  localResolve?: (result: OverlayResult) => void;
}

export interface OverlayProviderProps {
  /**
   * Channel client for host-initiated open/resolve broadcasts. Only consumed
   * together with {@link overlayService} — a window without the respond channel
   * (e.g. a detached window) never presents host-initiated overlays.
   */
  client?: ChannelClient;
  /**
   * RPC proxy for answering host-initiated overlays. Its presence marks THIS
   * window as the one that presents `context.overlay` (service-side) overlays;
   * windows without it (detached windows, web/edge builds) only serve
   * view-initiated overlays locally in-process (架构方案 §5.8 多窗取舍).
   */
  overlayService?: IOverlayService;
  /** All contributed views — used to resolve a hosted view's module and kind. */
  views: ResolvedView[];
  registry: UiRegistry;
  moduleService: IModuleService;
  onOpenChange?: (open: boolean) => void;
  /**
   * 把**本窗口**叫到前台（桌面 show + restore + focus，web 则 `window.focus()`）。
   * 只在 `openModal` 且宿主窗不在前台时调用。失败由这个函数自己记日志并 resolve，
   * 弹层照常入栈。若这次从另一扇窗口借走了焦点，resolve 一个归还函数，弹层关掉时调用。
   */
  presentWindow?: () => Promise<(() => Promise<void>) | undefined>;
  children: ReactNode;
}

/**
 * OverlaySurface host + opener provider (架构方案 §5.8). Holds one overlay stack
 * (supporting nested overlays, e.g. a confirm on top of a modal — not singleton),
 * exposes the {@link OverlayApi} openers to inline-dom views via context
 * (in-process, no round-trip), and also presents host-initiated overlays pushed
 * over the EventBus (mirroring the permission prompt), answering them through the
 * overlay RPC channel. Overlays portal above in-DOM surfaces naturally (an
 * `iframe` surface is ordinary DOM, no z-order suppression needed, 见 09).
 */
export function OverlayProvider({
  client,
  overlayService,
  views,
  registry,
  moduleService,
  onOpenChange,
  presentWindow,
  children,
}: OverlayProviderProps): ReactNode {
  const [stack, setStack] = useState<OverlayInstance[]>([]);
  const seq = useRef(0);
  const prepareNativeContentsSuppression =
    usePrepareNativeContentsSuppression();
  const presentWindowRef = useRef(presentWindow);
  presentWindowRef.current = presentWindow;
  const focusReturns = useRef(new Map<string, () => Promise<void>>());
  const focusSettled = useRef(new Set<string>());
  const hostIds = useRef(new Set<string>());

  const returnFocus = useCallback((key: string) => {
    const restore = focusReturns.current.get(key);
    focusReturns.current.delete(key);
    if (restore) {
      void restore();
      return;
    }
    focusSettled.current.add(key);
  }, []);

  const borrowFocus = useCallback((key: string, request: OverlayRequest) => {
    const present = presentWindowRef.current;
    if (!present || !shouldPresentHostWindow(request, document.hasFocus())) {
      return;
    }
    void present().then((restore) => {
      if (!restore) {
        return;
      }
      const giveBack = (): Promise<void> => {
        focusSettled.current.delete(key);
        return restore();
      };
      if (focusSettled.current.has(key)) {
        void giveBack();
        return;
      }
      focusReturns.current.set(key, giveBack);
    });
  }, []);

  const pushHostOverlay = useCallback(
    (id: string, request: OverlayRequest) => {
      if (hostIds.current.has(id)) {
        return;
      }
      hostIds.current.add(id);
      const key = `host-${id}`;
      setStack((prev) =>
        prev.some((item) => item.hostId === id)
          ? prev
          : [...prev, { key, hostId: id, request }],
      );
      borrowFocus(key, request);
    },
    [borrowFocus],
  );

  useLayoutEffect(() => {
    onOpenChange?.(stack.length > 0);
  }, [onOpenChange, stack.length]);

  // Settle an overlay: remove it from the stack and deliver the result — back to
  // the in-process caller (view-initiated) or over the RPC channel (host-initiated).
  const resolveInstance = useCallback(
    (key: string, result: OverlayResult) => {
      returnFocus(key);
      setStack((prev) => {
        const inst = prev.find((item) => item.key === key);
        if (!inst) {
          return prev;
        }
        if (inst.hostId) {
          hostIds.current.delete(inst.hostId);
          void overlayService?.respond(inst.hostId, result);
        } else {
          inst.localResolve?.(result);
        }
        return prev.filter((item) => item.key !== key);
      });
    },
    [overlayService, returnFocus],
  );

  // View-initiated openers (in-process): push an instance, resolve its Promise on
  // settle. Shared shape with the ext-host `context.overlay` (see module-manager).
  const api = useMemo<OverlayApi>(() => {
    const open = async (request: OverlayRequest): Promise<OverlayResult> => {
      await prepareNativeContentsSuppression();
      const key = `local-${String(++seq.current)}`;
      borrowFocus(key, request);
      return new Promise<OverlayResult>((resolve) => {
        setStack((prev) => [...prev, { key, request, localResolve: resolve }]);
      });
    };
    return {
      openModal: (options) =>
        open({ role: 'view', presenter: 'modal', ...options }),
      openDrawer: (options) =>
        open({ role: 'view', presenter: 'drawer', ...options }),
      openPopover: (options) =>
        open({ role: 'view', presenter: 'popover', ...options }),
      confirm: (options) =>
        open({ role: 'confirm', ...options }) as Promise<boolean>,
      alert: (options) => open({ role: 'alert', ...options }) as Promise<void>,
      prompt: (options) =>
        open({ role: 'prompt', ...options }) as Promise<string | null>,
      select: (options) =>
        open({ role: 'select', ...options }) as Promise<string | null>,
    };
  }, [borrowFocus, prepareNativeContentsSuppression]);

  // Host-initiated opens (ext-host `context.overlay`) arrive over the EventBus;
  // a resolved broadcast (this or another window answered) drops the instance.
  // Only the window wired with the respond channel (`overlayService`) presents
  // them — so a detached window (no channel) never double-shows a service-side
  // overlay; it still serves its own view-initiated overlays locally (§5.8).
  useEffect(() => {
    if (!client || !overlayService) {
      return;
    }
    let disposed = false;
    const disposeOpen = client.listen(OVERLAY_OPEN_EVENT, (data) => {
      const { id, request } = data as OverlayOpen;
      void prepareNativeContentsSuppression().then(() => {
        if (!disposed) {
          pushHostOverlay(id, request);
        }
      });
    });
    const disposeResolved = client.listen(OVERLAY_RESOLVED_EVENT, (data) => {
      const { id } = data as OverlayResolved;
      hostIds.current.delete(id);
      returnFocus(`host-${id}`);
      setStack((prev) => prev.filter((item) => item.hostId !== id));
    });
    return () => {
      disposed = true;
      disposeOpen();
      disposeResolved();
    };
  }, [
    client,
    overlayService,
    prepareNativeContentsSuppression,
    pushHostOverlay,
    returnFocus,
  ]);

  return (
    <OverlayContext.Provider value={api}>
      {children}
      {stack.map((instance) => (
        <OverlayItem
          key={instance.key}
          instance={instance}
          onResolve={resolveInstance}
          views={views}
          registry={registry}
          moduleService={moduleService}
        />
      ))}
    </OverlayContext.Provider>
  );
}

interface OverlayItemProps {
  instance: OverlayInstance;
  onResolve: (key: string, result: OverlayResult) => void;
  views: ResolvedView[];
  registry: UiRegistry;
  moduleService: IModuleService;
}

/** Render one overlay by its request role: declarative chrome or a hosted view. */
function OverlayItem({
  instance,
  onResolve,
  views,
  registry,
  moduleService,
}: OverlayItemProps): ReactNode {
  const { key, request } = instance;
  const resolve = useCallback(
    (result: OverlayResult) => onResolve(key, result),
    [onResolve, key],
  );

  if (request.role === 'confirm') {
    return (
      <ConfirmDialog
        open
        title={request.title}
        message={request.message}
        confirmText={request.confirmText}
        cancelText={request.cancelText}
        destructive={request.destructive}
        onConfirm={() => resolve(true)}
        onCancel={() => resolve(false)}
      />
    );
  }
  if (request.role === 'alert') {
    return (
      <AlertDialog
        open
        title={request.title}
        message={request.message}
        confirmText={request.confirmText}
        onClose={() => resolve(undefined)}
      />
    );
  }
  if (request.role === 'prompt') {
    return (
      <PromptDialog
        open
        title={request.title}
        message={request.message}
        defaultValue={request.defaultValue}
        placeholder={request.placeholder}
        confirmText={request.confirmText}
        cancelText={request.cancelText}
        onSubmit={(value) => resolve(value)}
        onCancel={() => resolve(null)}
      />
    );
  }
  if (request.role === 'select') {
    return (
      <SelectDialog
        open
        title={request.title}
        message={request.message}
        options={request.options}
        placeholder={request.placeholder}
        cancelText={request.cancelText}
        onSelect={(value) => resolve(value)}
        onCancel={() => resolve(null)}
      />
    );
  }

  return (
    <OverlayViewItem
      request={request}
      resolve={resolve}
      views={views}
      registry={registry}
      moduleService={moduleService}
    />
  );
}

interface OverlayViewItemProps {
  request: Extract<OverlayRequest, { role: 'view' }>;
  resolve: (result: OverlayResult) => void;
  views: ResolvedView[];
  registry: UiRegistry;
  moduleService: IModuleService;
}

/**
 * Host a module view inside a modal/drawer shell (headless: the platform gives
 * only the presenter behavior; the view draws its own UI). An inline view closes
 * itself via {@link SurfaceView}'s `overlayControl`; an iframe view closes via
 * its SDK overlay channel (host-initiated open → broker → OVERLAY_RESOLVED),
 * which drops the instance here.
 */
function OverlayViewItem({
  request,
  resolve,
  views,
  registry,
  moduleService,
}: OverlayViewItemProps): ReactNode {
  const view = views.find((candidate) => candidate.id === request.viewId);
  const dismissable = request.dismissable ?? true;

  // Fail closed (架构方案 §5.8): openModal/openDrawer only host a view declared
  // with `area: 'overlay'` and rendered as `inline` or `iframe` (an inline view
  // settles via `overlayControl`, an iframe via its SDK overlay channel). Reject
  // an unknown view (typo / not contributed) as well as a misuse that targets a
  // docked view (e.g. an editor view opened as modal) or an `external` control
  // panel — hosting those here would double-mount the surface or render it with
  // no resolve/dismiss channel. (`validateManifest` already rejects illegal
  // `overlay` × kind at load; this guards the open-path caller, which it cannot
  // see.) Resolve undefined so the awaiting caller never hangs.
  const hostable =
    view !== undefined &&
    view.area === 'overlay' &&
    (view.kind === 'inline' || view.kind === 'iframe') &&
    // A `popover` with no anchor has nothing to sit beside. Centring it anyway
    // would silently make it a modal — a presenter the caller did not ask for
    // — so this is rejected like any other misuse above.
    (request.presenter !== 'popover' || request.anchor !== undefined);
  useEffect(() => {
    if (!hostable) {
      resolve(undefined);
    }
  }, [hostable, resolve]);
  if (!view || !hostable) {
    return null;
  }

  const surface = (
    <SurfaceView
      viewId={view.id}
      moduleId={view.moduleId}
      registry={registry}
      moduleService={moduleService}
      kind={view.kind}
      url={view.url}
      overlayParams={request.params}
      overlayControl={{
        resolve: (result?: OverlayResult) => resolve(result),
        dismiss: () => resolve(undefined),
        dismissable,
      }}
    />
  );

  /**
   * Non-modal mount. Not a Dialog, and not a second Positioner.
   *
   * A dialog would dim the window and mark everything else inert. A platform
   * Positioner around this view only ever sees a zero-size host: the view
   * portals its own popup and places that popup from the rect it copied into
   * `params`. Positioning here pins an empty box and unmounts the view on the
   * outside press, before that popup's exit transition can finish. The view
   * calls `overlay.dismiss()` once the transition ends. `request.anchor` is
   * only the fail-closed check above: a popover without one is rejected
   * instead of becoming a modal. This branch also does not honor
   * `dismissable`; the hosted view reads `overlay.dismissable` and ignores
   * outside press and Escape when that is false.
   */
  if (request.presenter === 'popover' && request.anchor) {
    return surface;
  }

  if (request.presenter === 'drawer') {
    return (
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open && dismissable) {
            resolve(undefined);
          }
        }}
      >
        <SheetContent
          side={request.side ?? 'right'}
          showCloseButton={dismissable}
        >
          {request.title ? (
            <SheetHeader>
              <SheetTitle>{request.title}</SheetTitle>
            </SheetHeader>
          ) : null}
          {surface}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Modal
      open
      title={request.size === 'full' ? undefined : request.title}
      size={request.size}
      dismissable={dismissable}
      showClose={request.size !== 'full'}
      onOpenChange={(open) => {
        if (!open) {
          resolve(undefined);
        }
      }}
    >
      {surface}
    </Modal>
  );
}
